import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Input } from "@progress/kendo-react-inputs";
import { USER_CHIP_LABELS, buildUserChipVariableHandlers, isValidUserChipResult, resolveUserChipElementValue } from "../../utils/userChipValue.utils";

/**
 * Renders a user chip element with proper value resolution
 * @param {Object} element - Form element data
 * @param {Object} userProfileData - User profile information
 * @param {Function} onChange - Change handler function
 * @param {Object} submitDetails - Form submission details
 * @returns {JSX.Element} User chip input component
 * @author Abhishek Dhillon
 * @parent FormRenderer
 */
const UserChipRenderer = ({ element, userProfileData, onChange, submitDetails }) => {
  const user = useSelector((state) => state?.auth?.user);
  const { type } = element || {};
  const { userVariableId, fixedVariableId, showChipName: showChipNameRaw } = element?.properties || {};
  const showChipName = showChipNameRaw === false ? false : true;
  const { WorkInformations } = userProfileData || {};
  const userChip = WorkInformations?.find((workInfo) => workInfo?._id === userVariableId);
  const { Manager, JobRole, Department, OfficeLocation } = useSelector((state) => state?.auth?.user) || {};
  const isFormSubmitted = submitDetails?.id && submitDetails?.name;

  const variableHandlers = useMemo(
    () =>
      buildUserChipVariableHandlers({
        isFormSubmitted,
        submitDetails,
        user,
        userChip,
        department: Department,
        jobRole: JobRole,
        manager: Manager,
        officeLocation: OfficeLocation,
      }),
    [submitDetails, user, userChip, Department, JobRole, Manager, OfficeLocation, isFormSubmitted]
  );

  const result = useMemo(
    () =>
      resolveUserChipElementValue({
        element,
        elementType: type,
        fixedVariableId,
        userChip,
        variableHandlers,
      }),
    [element, type, fixedVariableId, userChip, variableHandlers]
  );

  useEffect(() => {
    if (onChange) {
      const validResult = isValidUserChipResult(result) ? result : "";
      onChange?.(validResult);
    }
  }, [result, onChange]);

  return (
    <div>
      {showChipName && (
        <label htmlFor={`${element?.id}-user-chip`} className='form-label'>
          {element?.name}
        </label>
      )}
      <Input
        id={`${element?.id}-user-chip`}
        key={element?.id}
        type='text'
        value={result?.label || userChip?.value || USER_CHIP_LABELS.NO_VALUE_SELECTED}
        disabled={true}
        className='w-100'
      />
    </div>
  );
};

export default UserChipRenderer;
